import { useState } from 'react';
import { format } from 'date-fns';
import { useGanttTaskSchedule } from './useGanttTaskSchedule';
import { detectScheduleConflicts } from '../adapters';
import type { GanttModel, ScheduleChange, Conflict } from '../types';

interface GanttScheduleGate {
  pendingChange: ScheduleChange | null;
  conflicts: Conflict[];
  /** Bump signal for the chart to discard a rejected in-memory edit. */
  revertNonce: number;
  onScheduleEditing: (change: ScheduleChange) => void;
  onScheduleChange: (change: ScheduleChange) => void;
  accept: () => void;
  reject: () => void;
}

/**
 * Owns the guarded reschedule flow: a live drag preview, client-side conflict
 * detection against the loaded links, and the accept/reject gate that decides
 * whether the optimistic PATCH commits. A clean (conflict-free) drop commits
 * immediately; a conflicting one holds until the user accepts or rejects.
 */
export function useGanttScheduleGate(projectId: string, model: GanttModel): GanttScheduleGate {
  const scheduleMutation = useGanttTaskSchedule(projectId);
  const [pendingChange, setPendingChange] = useState<ScheduleChange | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [revertNonce, setRevertNonce] = useState(0);

  function detect(change: ScheduleChange): Conflict[] {
    return detectScheduleConflicts(
      change.taskId,
      change.to.startDate,
      change.to.endDate,
      model.links,
      model.rows,
    );
  }

  function commit(change: ScheduleChange) {
    // Gantt bars are day-granular: commit a floating date-only PlannedDate.
    scheduleMutation.mutate({
      taskId: change.taskId,
      startDate: { date: format(change.to.startDate, 'yyyy-MM-dd') },
      dueDate: { date: format(change.to.endDate, 'yyyy-MM-dd') },
    });
    setPendingChange(null);
    setConflicts([]);
  }

  return {
    pendingChange,
    conflicts,
    revertNonce,
    onScheduleEditing: (change) => {
      setPendingChange(change);
      setConflicts(detect(change));
    },
    onScheduleChange: (change) => {
      const found = detect(change);
      if (found.length === 0) {
        commit(change);
        return;
      }
      // Hold the commit; the banner's accept/reject decides.
      setPendingChange(change);
      setConflicts(found);
    },
    accept: () => {
      if (pendingChange) commit(pendingChange);
    },
    reject: () => {
      setPendingChange(null);
      setConflicts([]);
      setRevertNonce((value) => value + 1);
    },
  };
}
